<template>
  <div class="advance-info">
    <app-state v-if="loading" state="loading" title="Загружаем счет-фактуру" />
    <template v-else>
      <page-section title="Аванс" description="Основание платежа, налоговая ставка и итоговые суммы.">
        <div class="advance-info__summary">
          <div><span>Сумма аванса</span><amount-cell :value="summary.summtotal" /></div>
          <div><span>В том числе НДС</span><amount-cell :value="summary.nalog" /></div>
          <div><span>Валюта</span><strong>{{ body.currency || '—' }}</strong></div>
        </div>
        <dl class="advance-info__details">
          <div><dt>Платежно-расчетный документ</dt><dd>{{ body.plat_ras || body.seller_doc || '—' }}</dd></div>
          <div><dt>Идентификатор госконтракта</dt><dd>{{ body.goscon || '—' }}</dd></div>
          <div><dt>Предмет</dt><dd>{{ body.is_service ? 'Услуги или работы' : 'Товары или имущественные права' }}</dd></div>
        </dl>
        <app-data-table
          title="Строки аванса"
          :rows="rows"
          :columns="columns"
          :count="rows.length"
          :state="rows.length ? 'ready' : 'empty'"
          state-title="Строки аванса отсутствуют"
          state-message="В сохраненном документе нет табличной части."
          storage-key="advance-invoice-info"
          data-testid="advance-invoice-rows"
        >
          <template #body-cell-tax="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
          <template #body-cell-total="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
        </app-data-table>
      </page-section>

      <div class="advance-info__parties">
        <page-section title="Продавец">
          <dl class="advance-info__details advance-info__details--single">
            <div><dt>Организация</dt><dd>{{ body.seller || '—' }}</dd></div>
            <div><dt>ИНН / КПП</dt><dd>{{ body.seller_inn || '—' }}</dd></div>
            <div><dt>Адрес</dt><dd>{{ body.seller_adr || '—' }}</dd></div>
          </dl>
        </page-section>
        <page-section title="Покупатель">
          <dl class="advance-info__details advance-info__details--single">
            <div><dt>Организация</dt><dd>{{ body.cust || '—' }}</dd></div>
            <div><dt>ИНН / КПП</dt><dd>{{ body.cust_inn || '—' }}</dd></div>
            <div><dt>Адрес</dt><dd>{{ body.cust_adr || '—' }}</dd></div>
          </dl>
        </page-section>
      </div>

      <vat-registration-panel :document="documentMeta" :history-id="$route.params.history_id || 'last'" @reload="loadDocument" />

      <page-section title="Подписи">
        <dl class="advance-info__details">
          <div><dt>Руководитель</dt><dd>{{ body.rukvod || '—' }}</dd></div>
          <div><dt>Главный бухгалтер</dt><dd>{{ body.glavbuh || '—' }}</dd></div>
          <div><dt>Индивидуальный предприниматель</dt><dd>{{ body.ip_name || '—' }}</dd></div>
          <div><dt>Реквизиты свидетельства ИП</dt><dd>{{ body.ip_req || '—' }}</dd></div>
        </dl>
      </page-section>
    </template>
  </div>
</template>

<script>
import AmountCell from '../../../../components/ui/AmountCell.vue'
import AppDataTable from '../../../../components/ui/AppDataTable.vue'
import AppState from '../../../../components/ui/AppState.vue'
import PageSection from '../../../../components/ui/PageSection.vue'
import VatRegistrationPanel from '../../../../components/documents/VatRegistrationPanel.vue'
import { vatOptionLabel } from '../../../../services/vatRates'

const columns = Object.freeze([
  { name: 'name', label: 'Наименование', align: 'left', field: 'name', required: true },
  { name: 'vat', label: 'Ставка НДС', align: 'left', field: (row) => vatOptionLabel(row.nds) || '—' },
  { name: 'tax', label: 'Сумма НДС', align: 'right', field: 'nalog' },
  { name: 'total', label: 'Сумма с НДС', align: 'right', field: 'summtotal' }
])

export default {
  name: 'document_info_factura_avance',
  components: { AmountCell, AppDataTable, AppState, PageSection, VatRegistrationPanel },
  props: { embeddedWorkbench: { type: Boolean, default: false } },
  data() {
    return { body: {}, documentMeta: {}, loading: true, infoRequestId: null, handlers: [], columns }
  },
  computed: {
    rows() { return Array.isArray(this.body.table_rows) ? this.body.table_rows : [] },
    summary() { return this.body.summary || { summtotal: 0, nalog: 0 } }
  },
  mounted() {
    this.listen('didReceiveDocumentInfo', this.onDocumentInfo)
    this.listen('connectionReady', this.loadDocument)
    if (this.connection.isAuthenticated) this.loadDocument()
  },
  beforeUnmount() { this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler)) },
  methods: {
    listen(event, handler) { this.emitter.on(event, handler); this.handlers.push([event, handler]) },
    loadDocument() {
      this.loading = true
      this.infoRequestId = this.connection.makeRequestId('advance-info')
      this.connection.getDocumentInfo(this.infoRequestId, this.$route.params.id, this.$route.params.history_id || 'last')
    },
    onDocumentInfo(response) {
      if (response.request_id !== this.infoRequestId || !response.data || !['avance', 'accept_factura_avance', 'factura_avance'].includes(response.data.document_type)) return
      this.documentMeta = response.data
      this.body = response.data.body || {}
      this.loading = false
    },
    onDeleteDocument() { this.connection.deleteDocument(this.connection.makeRequestId('advance-delete'), this.$route.params.id) },
    onRestoreDocument() { this.connection.restoreDocument(this.connection.makeRequestId('advance-restore'), this.$route.params.id) }
  }
}
</script>

<style scoped>
.advance-info { display: grid; gap: var(--sid-space-6); min-width: 0; }
.advance-info__summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin-bottom: var(--sid-space-5); }
.advance-info__summary div { display: grid; gap: var(--sid-space-1); min-width: 0; }
.advance-info__summary span, .advance-info__details dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.advance-info__details { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin: 0 0 var(--sid-space-5); }
.advance-info__details--single { grid-template-columns: 1fr; margin-bottom: 0; }
.advance-info__details div { min-width: 0; }
.advance-info__details dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
.advance-info__parties { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); }
@media (max-width: 899px) { .advance-info__parties { grid-template-columns: 1fr; } }
@media (max-width: 599px) { .advance-info__summary, .advance-info__details { grid-template-columns: 1fr; } }
</style>
